/**
 * The behaviour runtime loader (agnostic ingest AG-4, steps 2 and 4).
 *
 * Fetches `builder/data/behaviours/<id>.js`, syntax-checks it, and wraps it in
 * the owner-scoped prelude that contract C-8 requires. Every surface that can
 * render or export a stamped section awaits `ensureBehaviourRuntimes(state)`
 * first, so no path can reach a stamped widget without having decided what to
 * do about a runtime it could not load.
 *
 * ## The failure model, and why it is not site-effects'
 *
 * `site-effects-loader.js` catches a fetch failure, returns `''`, and caches
 * that for the life of the session (`:42`). For an OPTIONAL flourish that is a
 * defensible trade. For a REQUIRED runtime it is the worst available outcome: a
 * transient blip on the first render would permanently disable a widget, the
 * page would look fine, and an export taken minutes later would ship a site
 * whose tabs do nothing — with nothing, anywhere, having reported a problem.
 *
 * So: **successes are cached, failures never are.** A retry is a real retry. And
 * a missing runtime is not silence — it is a `BehaviourRuntimeMissingError` that
 * FAILS an export and degrades a preview to static with a toast.
 *
 * ## Why the prelude is emitted here rather than written into each runtime
 *
 * A runtime is selector-free by contract: every selector arrives in the
 * serialized `__pbBehaviour` config, and a class or attribute selector literal
 * in runtime source fails a scan. That only means something if the runtime also
 * cannot reach outside its host — otherwise a config-supplied selector is just a
 * document-wide query with extra steps. `__pbOwn` / `__pbControls` /
 * `__pbSectionRoot` are the whole query surface, they are emitted once per
 * bundle here, and `document.getElementById` is forbidden outright: it is
 * document-wide by definition, so on a page with two instances of one section it
 * returns the first one's element to both.
 */

import { loadAsset } from '../core/asset-loader.js';
import {
  BEHAVIOUR_ATTR,
  BEHAVIOUR_BOUND_PREFIX,
  STATIC_CAPTURE_ATTR,
  behaviourIdsInHtml,
  behaviourRuntimeConfig,
  behaviourRuntimeFile,
  behaviourStampsInHtml,
  behaviourIsShipped
} from '../shared/behaviour-contract.js';

/** Thrown when a stamped behaviour has no usable runtime. Never swallowed. */
export class BehaviourRuntimeMissingError extends Error {
  constructor(ids, detail) {
    const list = [...ids].join(', ');
    super(
      `behaviour runtime unavailable for: ${list}${detail ? ` (${detail})` : ''}. ` +
        `A stamped section cannot ship without its runtime — the exported site would carry the ` +
        `markup of a working widget and none of its behaviour.`
    );
    this.name = 'BehaviourRuntimeMissingError';
    this.ids = [...ids];
  }
}

// Successes only. A failed id is simply absent, so the next call re-fetches it.
const runtimeCache = new Map();
// In-flight promises, so ten sections stamped `tabs` cause one fetch. Removed on
// rejection — an in-flight map that keeps rejected promises is a failure cache
// wearing a different name.
const inFlight = new Map();

let assetLoader = loadAsset;

/**
 * Test seam. The builder runs in a browser and `loadAsset` resolves against an
 * R2-backed manifest; a Node suite has neither. Named with the underscore prefix
 * so it reads as machinery at every call site.
 */
export function __setBehaviourAssetLoader(fn) {
  assetLoader = typeof fn === 'function' ? fn : loadAsset;
}

/** Test seam: forget everything, including successes. */
export function __resetBehaviourRuntimeCache() {
  runtimeCache.clear();
  inFlight.clear();
}

function syntaxProblem(source) {
  try {
    new Function(source);
    return null;
  } catch (err) {
    return err.message;
  }
}

async function fetchRuntime(id) {
  const file = behaviourRuntimeFile(id);
  if (!file) throw new Error(`no runtime declared for behaviour ${id}`);
  const resp = await assetLoader(`data/behaviours/${file}`);
  const text = await resp.text();
  if (!text.trim()) throw new Error(`${file} is empty`);
  // A runtime that does not parse is a MISSING runtime, not a present one. The
  // alternative — shipping it and letting the browser fail — puts a syntax error
  // in the same bundle as every other behaviour on the page.
  const problem = syntaxProblem(text);
  if (problem) throw new Error(`${file} does not parse: ${problem}`);
  return text;
}

/**
 * Load the named runtimes. Returns `{ok, missing, errors}`; never throws, so a
 * caller decides what a miss means — an export fails, a preview degrades.
 */
export async function loadBehaviourRuntimes(ids = []) {
  const wanted = [...new Set(ids)].filter((id) => behaviourIsShipped(id));
  const missing = [];
  const errors = new Map();
  await Promise.all(
    wanted.map(async (id) => {
      if (runtimeCache.has(id)) return;
      let promise = inFlight.get(id);
      if (!promise) {
        promise = fetchRuntime(id);
        inFlight.set(id, promise);
      }
      try {
        const text = await promise;
        runtimeCache.set(id, text);
      } catch (err) {
        missing.push(id);
        errors.set(id, err && err.message ? err.message : String(err));
      } finally {
        // Whether it resolved or rejected, the attempt is over. On rejection
        // this is what makes the next call a real retry rather than a replay of
        // the same failure.
        if (inFlight.get(id) === promise) inFlight.delete(id);
      }
    })
  );
  missing.sort();
  return { ok: missing.length === 0, missing, errors };
}

/**
 * The runtime source for one id. Throws `BehaviourRuntimeMissingError` when it
 * was never loaded — callers that must not ship without it use this, and get an
 * exception rather than an empty string.
 */
export function getBehaviourRuntimeJS(id) {
  const text = runtimeCache.get(id);
  if (!text) throw new BehaviourRuntimeMissingError([id], 'not loaded');
  return text;
}

/** Has this runtime been loaded in this session? */
export function behaviourRuntimeLoaded(id) {
  return runtimeCache.has(id);
}

/**
 * The per-instance bind call for one section's HTML, or ''.
 *
 * ONE implementation, used by the export path and by the canvas, because the
 * alternative is two string builders that agree today. `loadedOnly` is the
 * difference between the two SURFACES, not between two copies of the logic: an
 * export must fail on a missing runtime, so it asks for every stamp and lets
 * `assembleBehaviourJS` throw; the canvas must degrade to static, so it asks
 * only for what loaded and silently omits the rest.
 *
 * Configs are sorted by id because export artifacts are hashed and
 * `export-lock-tests` runs the whole export twice.
 */
export function behaviourBindJS(html, { loadedOnly = false } = {}) {
  const configs = behaviourStampsInHtml(html)
    .filter((t) => behaviourIsShipped(t.id, t.variant))
    .filter((t) => !loadedOnly || runtimeCache.has(t.id))
    .map((t) => behaviourRuntimeConfig(t.id, t.variant))
    .filter(Boolean)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (!configs.length) return '';
  // The THIRD argument is the facade's own section root element, and it is what
  // makes the ownership boundary exist off-canvas at all. `__pbSectionRoot`
  // recognises `data-pb-section` / `data-pb-instance`, and export STRIPS both:
  // an exported carousel therefore computed a null root, and the ancestor walk
  // for its arrows ran to the document, acquiring the next button of the
  // section below it (AG-4/6 audit finding 4). `__getSectionRoot` is declared by
  // `buildFacadeIIFE`, which is the only thing this string is ever embedded in;
  // the typeof guard keeps it inert anywhere else rather than throwing.
  return (
    `\n__pbBindDoc(document, ${JSON.stringify(configs)}, ` +
    `typeof __getSectionRoot === 'function' ? __getSectionRoot() : null);`
  );
}

/** Which behaviour ids does this state's markup actually stamp? */
export function behaviourIdsForState(state) {
  const ids = new Set();
  const push = (section) => {
    if (!section) return;
    for (const id of behaviourIdsInHtml(section.html)) ids.add(id);
  };
  push(state?.sharedSections?.navigation);
  for (const page of state?.pages || []) for (const section of page?.sections || []) push(section);
  push(state?.sharedSections?.footer);
  return [...ids].sort();
}

/**
 * The one call every entrypoint makes before rendering or exporting. Resolves
 * to `{ok, missing, ids}`; the caller decides the consequence.
 */
export async function ensureBehaviourRuntimes(state) {
  const ids = behaviourIdsForState(state);
  if (ids.length === 0) return { ok: true, missing: [], ids: [], errors: new Map() };
  const result = await loadBehaviourRuntimes(ids);
  return { ...result, ids };
}

// ---------------------------------------------------------------------------
// The owner-scoped prelude (contract C-8)
// ---------------------------------------------------------------------------

/**
 * Emitted ONCE per bundle, before any runtime text. Everything a runtime is
 * allowed to query with.
 *
 * `__pbOwn(host, sel)` — descendants of `host` matching `sel`, minus anything
 * that belongs to a NESTED host of the SAME behaviour. Same behaviour, not any
 * behaviour: C-8 admits distinct nested widgets, so a carousel inside a tab pane
 * keeps its own slides while a `.w-tabs` inside a `.w-tabs` takes its own links.
 * The audit's reproduction is exactly this — tabs containing tabs, where an
 * unscoped `querySelectorAll` gives the outer host both sets and the first click
 * activates a pane in the wrong widget.
 *
 * `__pbControls(host, cfg)` — the bounded lookup for controls that live OUTSIDE
 * the host (`controlsScope: 'ancestor'`; a carousel's arrows are commonly
 * siblings of the track). The walk stops at the SECTION ROOT, never at the
 * document, and **ambiguity means no bind**: two candidate sets, or an
 * `aria-controls` that resolves to zero or several elements, returns null. A
 * behaviour that binds the wrong control is worse than one that binds nothing —
 * the second is visible, the first moves someone else's widget.
 *
 * `__pbSectionRoot(el, given)` — the section boundary, and it can return null. It
 * never falls back to `document.body`: a fallback would turn "this element is not
 * in a section" into "the whole page is the section", which is how a bounded walk
 * becomes an unbounded one. `given` is the facade's OWN root element, threaded in
 * by `__pbBindDoc`'s third argument, and it is what makes the boundary exist off
 * the canvas at all — an export strips `data-pb-section` and `data-pb-instance`,
 * so the attribute walk found nothing there and `__pbControls` ran to the
 * document (audit finding 4). When no boundary can be established, a bounded
 * search now returns HOST-scoped hits rather than searching anyway.
 */
export const BEHAVIOUR_PRELUDE = `/* PageStitch behaviour prelude (C-8) */
var __pbAttr = ${JSON.stringify(BEHAVIOUR_ATTR)};
var __pbBoundPrefix = ${JSON.stringify(BEHAVIOUR_BOUND_PREFIX)};
var __pbStaticAttr = ${JSON.stringify(STATIC_CAPTURE_ATTR)};
function __pbStampHasId(value, id) {
  var tokens = String(value || '').split(/\\s+/);
  for (var i = 0; i < tokens.length; i++) {
    var t = tokens[i];
    if (!t) continue;
    if (t === id) return true;
    if (t.slice(0, id.length + 1) === id + ':') return true;
  }
  return false;
}
// Does this stamp name THIS config -- id AND variant? A bare id token accepts
// any variant of that id; a variant token accepts only its own config. Matching
// on id alone bound a splide host with the swiper config the moment both were
// on one page, marked it bound, and its own config never reached it (AG-6).
function __pbStampMatches(value, cfg) {
  var tokens = String(value || '').split(/\\s+/);
  for (var i = 0; i < tokens.length; i++) {
    var t = tokens[i];
    if (!t) continue;
    if (t === cfg.id) return true;
    if (cfg.variant && t === cfg.id + ':' + cfg.variant) return true;
  }
  return false;
}
// The boundary. \`given\` is the facade's own section root element, passed in by
// the bind call: export STRIPS data-pb-section/data-pb-instance, so the
// attribute walk alone returns null off-canvas and every bounded search
// silently became unbounded. An element outside \`given\` is never inside the
// section, whatever its attributes say.
function __pbSectionRoot(el, given) {
  var n = el;
  while (n && n.nodeType === 1) {
    if (given && n === given) return n;
    if (!given && (n.hasAttribute('data-pb-section') || n.hasAttribute('data-pb-instance'))) return n;
    n = n.parentElement;
  }
  return null;
}
function __pbOwn(host, sel, id) {
  var out = [];
  if (!host || !host.querySelectorAll || !sel) return out;
  var all;
  try { all = host.querySelectorAll(sel); } catch (e) { return out; }
  for (var i = 0; i < all.length; i++) {
    var el = all[i];
    var p = el.parentElement;
    var owned = true;
    while (p && p !== host) {
      if (p.hasAttribute && p.hasAttribute(__pbAttr) && __pbStampHasId(p.getAttribute(__pbAttr), id)) {
        owned = false;
        break;
      }
      p = p.parentElement;
    }
    if (owned) out.push(el);
  }
  return out;
}
function __pbControls(host, sel, id, scope, given) {
  if (!host || !sel) return [];
  if (scope !== 'ancestor') return __pbOwn(host, sel, id);
  var root = __pbSectionRoot(host, given);
  // NO BOUNDARY, NO SEARCH. A walk with nothing to stop it reaches the document
  // and binds another section's control; refusing to leave the host is the only
  // answer that cannot be wrong. Same when the host IS the root -- there is no
  // "outside the host but inside the section" for it, and starting at
  // host.parentElement was already a step past the boundary.
  if (!root || root === host) return __pbOwn(host, sel, id);
  var n = host.parentElement;
  // The NEAREST ancestor with hits owns them, and the walk stops there. It has
  // to: every ancestor above that one contains the same elements, so a walk
  // that kept going and called a second sighting "ambiguous" returned nothing
  // for every control that exists at all (AG-6 found this when the first
  // carousel asked for its arrows). Ambiguity is a matter for the RUNTIME,
  // which knows what a role is and can see two candidates for one of them.
  while (n && n.nodeType === 1) {
    var hits = __pbOwn(n, sel, id);
    if (hits.length) return hits;
    if (n === root) break;
    n = n.parentElement;
  }
  return [];
}
function __pbAriaControls(el, root) {
  if (!el || !el.getAttribute) return null;
  var raw = el.getAttribute('aria-controls');
  if (!raw) return null;
  var ids = raw.split(/\\s+/).filter(Boolean);
  if (ids.length !== 1) return null;
  var scope = root || __pbSectionRoot(el);
  if (!scope || !scope.querySelectorAll) return null;
  var matches;
  try { matches = scope.querySelectorAll('[id="' + ids[0].replace(/(["\\\\])/g, '\\\\$1') + '"]'); }
  catch (e) { return null; }
  return matches.length === 1 ? matches[0] : null;
}
// Does el's aria-controls point at want?
//   true  -- it resolves, uniquely and inside the boundary, to want
//   false -- it makes no claim on want at all
//   null  -- it NAMES want's id and does NOT resolve to it: two elements share
//            that id, or it lists several targets. C-8 says an ambiguous
//            relation binds nothing, and that verdict has to outlive the
//            runtime's class-selector fallback, or the fallback re-adopts the
//            control the ARIA check just refused (audit finding 5).
function __pbAriaOwns(el, want, root) {
  if (!el || !want || !el.getAttribute) return false;
  var raw = el.getAttribute('aria-controls');
  if (!raw) return false;
  if (__pbAriaControls(el, root) === want) return true;
  // It declares a relation and that relation is not to US -- either it names
  // ANOTHER element, or it does not resolve at all. Both are refusals. The
  // first version only refused the second, so a button explicitly declaring
  // aria-controls='tb' was still adopted by sibling track 'ta' through the
  // class fallback: an explicit statement of ownership read as no statement.
  return null;
}
// The ONE control for one role, or null. \`find(sel)\` is the caller's bounded
// lookup; \`want\` is the element the control must operate.
//
// An aria relation that RESOLVES to want wins, and only if exactly one
// candidate carries it. Otherwise the registry's class selector is a FALLBACK
// -- never an override: a fallback whose own aria-controls names want without
// resolving to it has already declared its target unknowable, and binding it on
// a class match is how one arrow drove two same-id sibling tracks.
function __pbRoleControl(find, want, ariaSel, sel, re, root) {
  var hits = [];
  if (ariaSel && re) {
    var all = find(ariaSel);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (__pbAriaOwns(el, want, root) !== true) continue;
      if (re.test((el.getAttribute('aria-label') || '') + ' ' + (el.className || ''))) hits.push(el);
    }
  }
  if (hits.length === 1) return hits[0];
  // TWO resolving candidates for ONE role is ambiguity, and C-8 says an
  // ambiguous relation binds nothing. Falling through to the class selector
  // picked whichever of them happened to carry the class, which is a coin toss
  // wearing a rule.
  if (hits.length > 1) return null;
  var back = find(sel);
  if (back.length !== 1) return null;
  return __pbAriaOwns(back[0], want, root) === null ? null : back[0];
}
function __pbBound(host, id) {
  return host.hasAttribute(__pbBoundPrefix + id);
}
function __pbMarkBound(host, id) {
  host.setAttribute(__pbBoundPrefix + id, '1');
}
function __pbRefusesHost(host, guards) {
  for (var i = 0; i < (guards || []).length; i++) {
    if (host.hasAttribute(guards[i])) return true;
  }
  return false;
}
function __pbStaticCapture(doc) {
  var root = doc && doc.documentElement;
  return !!(root && root.hasAttribute(__pbStaticAttr));
}
function __pbReducedMotion(win) {
  try { return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); }
  catch (e) { return false; }
}
var __pbBehaviours = {};
function __pbDefine(id, fn) { __pbBehaviours[id] = fn; }
function __pbHosts(root, cfg) {
  var out = [];
  if (!root || !root.querySelectorAll) return out;
  var match = typeof cfg === 'string' ? { id: cfg, variant: null } : cfg;
  if (root.hasAttribute && root.hasAttribute(__pbAttr) && __pbStampMatches(root.getAttribute(__pbAttr), match)) {
    out.push(root);
  }
  var all;
  try { all = root.querySelectorAll('[' + __pbAttr + ']'); } catch (e) { return out; }
  for (var i = 0; i < all.length; i++) {
    if (__pbStampMatches(all[i].getAttribute(__pbAttr), match)) out.push(all[i]);
  }
  return out;
}
function __pbBindHosts(hosts, cfg, doc, given) {
  var id = cfg.id;
  var fn = __pbBehaviours[id];
  if (!fn) return 0;
  var bound = 0;
  for (var i = 0; i < hosts.length; i++) {
    var host = hosts[i];
    if (__pbBound(host, id)) continue;
    if (__pbRefusesHost(host, cfg.legacyGuards)) continue;
    __pbMarkBound(host, id);
    try {
      fn(host, cfg.config || {}, {
        own: function (h, sel) { return __pbOwn(h, sel, id); },
        controls: function (h, sel) { return __pbControls(h, sel, id, cfg.controlsScope || 'host', given); },
        // Both of these carry the facade's boundary, so a runtime cannot
        // accidentally resolve a relation against the whole document by
        // passing no root of its own.
        ariaControls: function (el, root) { return __pbAriaControls(el, root || __pbSectionRoot(el, given)); },
        ariaOwns: function (el, want) { return __pbAriaOwns(el, want, __pbSectionRoot(el, given)); },
        roleControl: function (find, want, ariaSel, sel, re) {
          return __pbRoleControl(find, want, ariaSel, sel, re, __pbSectionRoot(want, given));
        },
        sectionRoot: function (el) { return __pbSectionRoot(el, given); },
        staticCapture: __pbStaticCapture,
        reducedMotion: __pbReducedMotion,
        // The document the binder was handed. Inside a facade this is the scoped
        // proxy, whose body proxy honours the builder's scroll pin -- a runtime
        // that writes a body class goes through it rather than ownerDocument.
        document: doc || (host.ownerDocument || null)
      });
      bound++;
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[PB] behaviour ' + id + ' failed to bind:', e);
      }
    }
  }
  return bound;
}
function __pbBind(id, root, cfg) {
  var full = cfg || { id: id };
  return __pbBindHosts(__pbHosts(root, full), full, root && root.ownerDocument, root || null);
}
function __pbBindDoc(doc, cfgs, given) {
  // Called from INSIDE a section facade, so doc is the facade's scoped document
  // proxy and this query already resolves to this instance's hosts only
  // (#exportId[attr], #exportId [attr] -- self or descendant). Reusing the
  // facade's scoping is the point: a second scoping mechanism beside it is a
  // second thing that can disagree about which instance an element belongs to,
  // and that disagreement is what put instance A's listeners inside instance B
  // before the facade existed.
  //
  // NOTE FOR ANYONE EDITING THIS PRELUDE: it is a JS template literal, so a
  // backtick anywhere inside it -- including in a comment -- ends the string.
  var total = 0;
  for (var i = 0; i < cfgs.length; i++) {
    var cfg = cfgs[i];
    var all;
    try { all = doc.querySelectorAll('[' + __pbAttr + ']'); } catch (e) { continue; }
    var hosts = [];
    for (var j = 0; j < all.length; j++) {
      if (__pbStampMatches(all[j].getAttribute(__pbAttr), cfg)) hosts.push(all[j]);
    }
    total += __pbBindHosts(hosts, cfg, doc, given || null);
  }
  return total;
}
`;

/**
 * The full behaviour bundle for a state: the prelude plus every loaded runtime,
 * in a stable order.
 *
 * Deterministic by id, because export artifacts are hashed and a set-iteration
 * order would make `export-lock-tests` run-twice fail for no reason anyone could
 * find.
 */
export function assembleBehaviourJS(ids = []) {
  const wanted = [...new Set(ids)].filter((id) => behaviourIsShipped(id)).sort();
  if (wanted.length === 0) return '';
  const parts = [BEHAVIOUR_PRELUDE];
  for (const id of wanted) parts.push(getBehaviourRuntimeJS(id));
  return parts.join('\n');
}
